/**
 * Guest and contact names, and the Customer Link record a picked contact carries.
 *
 * The service stores a guest's name as `firstName` / `lastName` (AWSECEventHubModel CR-315061624,
 * which replaced `guestName` and `Contact.name`). Records saved before the split come back with
 * the whole name in `firstName` and no `lastName`, so they need no special case here.
 */
import type { Contact, GuestInformation } from '@amzn/awseceventhub-client';

const CJK = /[㐀-鿿豈-﫿]/;

/**
 * A person's name for display, from its parts. Chinese names read family name first with no
 * space ("王芳"); others given name first with a space ("Fang Wang"). Either part may be missing.
 */
export function displayName(first: string | undefined, last: string | undefined): string | undefined {
  const f = first?.trim() ?? '';
  const l = last?.trim() ?? '';
  if (!f && !l) return undefined;
  if (!f || !l) return f || l;
  return CJK.test(f + l) ? `${l}${f}` : `${f} ${l}`;
}

/** The guest's name for display. */
export function guestNameOf(guest: GuestInformation | undefined): string | undefined {
  return displayName(guest?.firstName, guest?.lastName);
}

/** A contact's name for display. */
export function contactNameOf(contact: Contact | undefined): string | undefined {
  return displayName(contact?.firstName, contact?.lastName);
}

/** A contact's name parts, for filling the form's First Name / Last Name. */
export function contactNameParts(contact: Contact | undefined): {
  firstName?: string;
  lastName?: string;
} {
  if (!contact) return {};
  return {
    firstName: contact.firstName?.trim() || undefined,
    lastName: contact.lastName?.trim() || undefined,
  };
}

/** The Customer Link record a contact is, to send back as GuestInformation.objectId / objectType. */
export function contactRef(contact: Contact | undefined): { objectId?: string; objectType?: string } {
  if (!contact) return {};
  return { objectId: contact.objectId, objectType: contact.objectType };
}
